import React, { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import StepIndicator from './components/StepIndicator';
import Step1FounderInfo from './components/Step1FounderInfo';
import Step2BusinessDetails from './components/Step2BusinessDetails';
import Step3InvestmentAsk from './components/Step3InvestmentAsk';
import Step4Review from './components/Step4Review';
import SuccessScreen from './components/SuccessScreen';

const TOTAL_STEPS = 4;

const INITIAL_FORM = {
  // Step 1
  fullName: '',
  email: '',
  phone: '',
  linkedinUrl: '',
  profilePhoto: null,
  profilePhotoPreview: null,
  city: '',
  state: '',
  // Step 2
  businessName: '',
  websiteUrl: '',
  sector: '',
  fundingStage: '',
  yearFounded: '',
  employees: '',
  description: '',
  // Step 3
  minInvestment: '',
  fundingGoal: '',
  useOfFunds: '',
  projectedRoi: '',
  investmentTimeline: '',
  isWomenLed: false,
  pitchDeck: null,
  pitchDeckName: '',
};

// Toast component
function Toast({ toasts }) {
  return (
    <div className="fixed top-4 right-4 z-[9999] flex flex-col gap-2 pointer-events-none">
      {toasts?.map(t => (
        <div
          key={t?.id}
          className="flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg pointer-events-auto"
          style={{
            background: t?.type === 'success' ? '#F0FDF4' : '#FEF2F2',
            border: `1px solid ${t?.type === 'success' ? '#BBF7D0' : '#FECACA'}`,
            animation: 'slideInRight 0.3s ease-out forwards'
          }}
        >
          {t?.type === 'success' ? (
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <circle cx="9" cy="9" r="8" fill="#22C55E"/>
              <path d="M5 9l2.5 2.5L13 6" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          ) : (
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <circle cx="9" cy="9" r="8" fill="#EF4444"/>
              <path d="M9 5.5v4M9 12h.01" stroke="#fff" strokeWidth="1.8" strokeLinecap="round"/>
            </svg>
          )}
          <p className="text-sm font-medium" style={{ color: t?.type === 'success' ? '#15803D' : '#DC2626' }}>{t?.message}</p>
        </div>
      ))}
    </div>
  );
}

export default function BusinessApplicationModal() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [referenceNumber, setReferenceNumber] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((message, type = 'success') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev?.filter(t => t?.id !== id)), 3000);
  }, []);

  const updateForm = useCallback((fields) => {
    setFormData(prev => ({ ...prev, ...fields }));
    // Clear errors for changed fields
    const fieldKeys = Object.keys(fields);
    setErrors(prev => {
      const next = { ...prev };
      fieldKeys?.forEach(k => {
        if (fields?.[k] !== undefined && fields?.[k] !== '') delete next?.[k];
      });
      return next;
    });
    // Toast for file uploads
    if (fields?.profilePhoto && fields?.profilePhotoPreview) {
      addToast('Profile photo uploaded successfully', 'success');
    }
    if (fields?.pitchDeck && fields?.pitchDeckName) {
      addToast('Pitch deck uploaded successfully', 'success');
    }
    if (fields?.profilePhoto === null && fields?.profilePhotoPreview === null && 'profilePhoto' in fields) {
      addToast('Photo upload failed — please try again', 'error');
    }
    if (fields?.pitchDeck === null && fields?.pitchDeckName === '' && 'pitchDeck' in fields && Object.keys(fields)?.length === 2) {
      // cleared by user, no toast
    }
  }, [addToast]);

  const validateStep = (step) => {
    const errs = {};
    if (step === 1) {
      if (!formData?.fullName?.trim()) errs.fullName = 'Full name is required';
      if (!formData?.email?.trim()) errs.email = 'Email is required';
      else if (!/^[^@]+@[^@]+\.[^@]+$/?.test(formData?.email)) errs.email = 'Enter a valid email';
      if (!formData?.phone?.trim()) errs.phone = 'Phone number is required';
      if (!formData?.city?.trim()) errs.city = 'City is required';
      if (!formData?.state?.trim()) errs.state = 'State is required';
    }
    if (step === 2) {
      if (!formData?.businessName?.trim()) errs.businessName = 'Business name is required';
      if (!formData?.sector) errs.sector = 'Please select a sector';
      if (!formData?.fundingStage) errs.fundingStage = 'Please select a funding stage';
      if (!formData?.description?.trim()) errs.description = 'Business description is required';
    }
    if (step === 3) {
      if (!formData?.minInvestment) errs.minInvestment = 'Minimum investment is required';
      if (!formData?.fundingGoal) errs.fundingGoal = 'Funding goal is required';
      if (!formData?.useOfFunds?.trim()) errs.useOfFunds = 'Use of funds is required';
      if (!formData?.projectedRoi) errs.projectedRoi = 'Projected ROI is required';
      if (!formData?.investmentTimeline) errs.investmentTimeline = 'Investment timeline is required';
      if (!formData?.isWomenLed) errs.isWomenLed = 'You must confirm this is a women-led business';
    }
    return errs;
  };

  const handleContinue = () => {
    const errs = validateStep(currentStep);
    if (Object.keys(errs)?.length > 0) {
      setErrors(errs);
      return;
    }
    setCurrentStep(s => s + 1);
  };

  const handleBack = () => setCurrentStep(s => s - 1);

  const handleRetry = () => {
    setSubmitError('');
  };

  const handleSubmit = async (confirmAccurate, confirmTerms) => {
    if (!confirmAccurate || !confirmTerms) return;
    setSubmitting(true);
    setSubmitError('');
    try {
      let profilePhotoUrl = null;
      let pitchDeckUrl = null;

      // Upload profile photo
      if (formData?.profilePhoto) {
        const ext = formData?.profilePhoto?.name?.split('.')?.pop();
        const path = `photos/${Date.now()}-${Math.random()?.toString(36)?.slice(2)}.${ext}`;
        const { error: uploadErr } = await supabase?.storage?.from('showcase-photos')?.upload(path, formData?.profilePhoto, { cacheControl: '3600', upsert: false });
        if (!uploadErr) {
          const { data: urlData } = supabase?.storage?.from('showcase-photos')?.getPublicUrl(path);
          profilePhotoUrl = urlData?.publicUrl || null;
        }
      }

      // Upload pitch deck
      if (formData?.pitchDeck) {
        const path = `decks/${Date.now()}-${Math.random()?.toString(36)?.slice(2)}.pdf`;
        const { error: deckErr } = await supabase?.storage?.from('showcase-pitchdecks')?.upload(path, formData?.pitchDeck, { cacheControl: '3600', upsert: false });
        if (!deckErr) {
          pitchDeckUrl = path;
        }
      }

      const refNum = `WIN-${Date.now()?.toString(36)?.toUpperCase()}-${Math.random()?.toString(36)?.slice(2, 6)?.toUpperCase()}`;

      const { error: insertErr } = await supabase?.from('showcase_applications')?.insert({
        founder_name: formData?.fullName,
        email: formData?.email,
        phone: formData?.phone,
        linkedin_url: formData?.linkedinUrl,
        profile_photo_url: profilePhotoUrl,
        location: `${formData?.city}, ${formData?.state}`,
        business_name: formData?.businessName,
        website_url: formData?.websiteUrl,
        sector: formData?.sector,
        funding_stage: formData?.fundingStage,
        year_founded: formData?.yearFounded,
        employees: formData?.employees,
        description: formData?.description,
        min_investment: parseFloat(formData?.minInvestment) || null,
        funding_goal: parseFloat(formData?.fundingGoal) || null,
        use_of_funds: formData?.useOfFunds,
        projected_roi: parseFloat(formData?.projectedRoi) || null,
        investment_timeline: formData?.investmentTimeline,
        is_women_led: formData?.isWomenLed,
        pitch_deck_url: pitchDeckUrl,
        status: 'pending',
        submitted_at: new Date()?.toISOString(),
      });

      if (insertErr) throw insertErr;

      setReferenceNumber(refNum);
      setSubmitted(true);
    } catch (err) {
      setSubmitError(err?.message || 'Submission failed. Please check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <SuccessScreen
        firstName={formData?.fullName?.split(' ')?.[0]}
        email={formData?.email}
        referenceNumber={referenceNumber}
        onReturn={() => navigate('/')}
      />
    );
  }

  return (
    <div className="min-h-screen flex flex-col" style={{ background: '#fff' }}>
      <style>{`@keyframes slideInRight { from { opacity: 0; transform: translateX(40px); } to { opacity: 1; transform: translateX(0); } }`}</style>

      <Toast toasts={toasts} />

      {/* Header */}
      <div className="sticky top-0 z-50 bg-white border-b border-gray-100 shadow-sm">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="/assets/images/Golden_coins_Win_Logo-1772863636273.png"
              alt="WIN Logo"
              className="h-9 w-auto"
            />
            <div>
              <p className="text-xs font-semibold tracking-widest uppercase" style={{ color: '#D4A520' }}>WIN Network</p>
              <p className="text-sm font-bold" style={{ color: '#0B1F3B' }}>Business Showcase Application</p>
            </div>
          </div>
          <button
            onClick={() => navigate('/')}
            className="w-9 h-9 flex items-center justify-center rounded-full border border-gray-200 hover:bg-gray-50 transition-colors"
            aria-label="Close"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M12 4L4 12M4 4l8 8" stroke="#0B1F3B" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>
        {/* Progress bar */}
        <div className="h-1 bg-gray-100">
          <div
            className="h-full transition-all duration-500"
            style={{ width: `${(currentStep / TOTAL_STEPS) * 100}%`, background: '#D4A520' }}
          />
        </div>
      </div>

      {/* Step Indicator */}
      <div className="max-w-3xl mx-auto w-full px-4 pt-8 pb-2">
        <StepIndicator currentStep={currentStep} totalSteps={TOTAL_STEPS} />
      </div>

      {/* Form Content */}
      <div className="flex-1 max-w-3xl mx-auto w-full px-4 py-6">
        {currentStep === 1 && (
          <Step1FounderInfo
            formData={formData}
            errors={errors}
            onChange={updateForm}
            onContinue={handleContinue}
            onSetErrors={setErrors}
          />
        )}
        {currentStep === 2 && (
          <Step2BusinessDetails
            formData={formData}
            errors={errors}
            onChange={updateForm}
            onContinue={handleContinue}
            onBack={handleBack}
            onSetErrors={setErrors}
          />
        )}
        {currentStep === 3 && (
          <Step3InvestmentAsk
            formData={formData}
            errors={errors}
            onChange={updateForm}
            onContinue={handleContinue}
            onBack={handleBack}
            onSetErrors={setErrors}
          />
        )}
        {currentStep === 4 && (
          <Step4Review
            formData={formData}
            onSubmit={handleSubmit}
            onBack={handleBack}
            submitting={submitting}
            submitError={submitError}
            onRetry={handleRetry}
          />
        )}
      </div>
    </div>
  );
}
